import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import NumberModal from "./NumberModal";

const useStyles = makeStyles(theme => ({
  root: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: theme.app.chatBg },
  card: {
    width: 460,
    maxWidth: "100%",
    background: "#fff",
    border: `1px solid ${theme.app.border}`,
    borderRadius: 14,
    padding: 28,
    boxShadow: "0 10px 34px rgba(20,30,40,0.07)",
  },
  badge: { width: 40, height: 40, borderRadius: 10, background: theme.app.dark, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 },
  badgeDot: { width: 12, height: 12, borderRadius: "50%", background: theme.app.lime },
  h1: { fontFamily: theme.app.heading, fontSize: 20, fontWeight: 600, color: theme.app.ink, margin: 0 },
  sub: { fontSize: 13.5, color: theme.app.textSecondary, margin: "8px 0 0", lineHeight: 1.55 },

  who: { display: "flex", flexDirection: "column", gap: 2, margin: "18px 0", padding: "12px 14px", background: theme.app.hover, border: `1px solid ${theme.app.border}`, borderRadius: 10 },
  whoName: { fontSize: 14, fontWeight: 600, color: theme.app.ink },
  whoEmail: { fontSize: 12.5, color: theme.app.textTertiary },

  label: { display: "block", fontSize: 12.5, fontWeight: 500, color: theme.app.ink, margin: "16px 0 6px" },
  fixed: { padding: "12px 14px", border: `1px solid ${theme.app.borderInput}`, borderRadius: 8, fontSize: 16, fontWeight: 600, color: theme.app.ink, fontFamily: theme.app.mono, letterSpacing: ".01em" },
  fixedNote: { fontSize: 12, color: theme.app.textTertiary, marginTop: 6 },
  select: { width: "100%", padding: "0 12px", height: 44, border: `1px solid ${theme.app.borderInput}`, borderRadius: 8, fontSize: 14, background: "#fff", color: theme.app.ink, "&:focus": { outline: "none", borderColor: theme.app.dark, boxShadow: "0 0 0 3px rgba(206,255,0,.35)" } },
  linkBtn: { background: "none", border: "none", padding: 0, marginTop: 8, color: theme.app.textSecondary, fontSize: 12.5, fontWeight: 500, textDecoration: "underline", cursor: "pointer" },

  btn: { width: "100%", height: 44, marginTop: 22, background: theme.app.dark, color: "#fff", border: "none", borderRadius: 8, fontSize: 14.5, fontWeight: 600, cursor: "pointer", "&:hover": { background: "#1f262c" }, "&:disabled": { opacity: 0.5, cursor: "default" } },
  btnGhost: { background: "#fff", color: theme.app.ink, border: `1px solid ${theme.app.border}`, "&:hover": { background: theme.app.lineSoft } },

  step: { display: "flex", alignItems: "center", gap: 10, padding: "9px 0", fontSize: 14 },
  dot: { width: 18, height: 18, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff" },

  urlBox: { display: "flex", gap: 8, marginTop: 8 },
  urlInput: { flex: 1, padding: "0 12px", height: 42, border: `1px solid ${theme.app.borderInput}`, borderRadius: 8, fontSize: 13, fontFamily: theme.app.mono, color: theme.app.ink, background: theme.app.hover },
  copy: { padding: "0 16px", height: 42, border: `1px solid ${theme.app.border}`, borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500, color: theme.app.ink, "&:hover": { background: theme.app.lineSoft } },

  warn: { marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "#FBF3E4", color: "#B7791F", fontSize: 12.5, lineHeight: 1.5 },
  det: { marginTop: 10, border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: "11px 14px", fontSize: 13.5, color: theme.app.textSecondary },
  detSummary: { fontWeight: 600, cursor: "pointer", color: theme.app.ink, fontSize: 13.5 },
  ol: { margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.7 },
  err: { fontSize: 13.5, color: theme.app.danger, marginTop: 12 },
}));

const STEPS = ["Provisionando sua central de voz", "Importando o número escolhido", "Configurando o número de origem"];
const normDigits = v => String(v || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");

const McpSetup = () => {
  const c = useStyles();
  const location = useLocation();
  const urlDid = normDigits(new URLSearchParams(location.search).get("did") || "");

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [did, setDid] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [phase, setPhase] = useState("auth"); // auth | working | done | error
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/mcp");
        setInfo(data);
        const nums = data.numbers || [];
        // did da URL > default atual > primeiro
        const chosen = (urlDid && nums.find(n => n.number === urlDid) && urlDid) ||
          (nums.find(n => n.isDefault) || {}).number ||
          (nums[0] || {}).number || urlDid || "";
        setDid(chosen);
      } catch (e) {
        setError((e.response && e.response.data && e.response.data.error) || "Não foi possível carregar.");
        setPhase("error");
      } finally {
        setLoading(false);
      }
    })();
    return () => timer.current && clearInterval(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enable = async () => {
    setPhase("working");
    setStepIdx(0);
    timer.current = setInterval(() => setStepIdx(i => Math.min(i + 1, STEPS.length - 1)), 1400);
    try {
      const { data } = await api.post("/me/mcp/enable", { did: did || undefined });
      setResult(data);
      setPhase("done");
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || "Falha ao habilitar.");
      setPhase("error");
    } finally {
      clearInterval(timer.current);
    }
  };

  const copy = v => navigator.clipboard && navigator.clipboard.writeText(v);
  const hasNumbers = info && info.numbers && info.numbers.length;
  const selectedInfo = ((info && info.numbers) || []).find(n => n.number === did);

  if (loading) return <div className={c.root}><CircularProgress size={28} /></div>;

  return (
    <div className={c.root}>
      <div className={c.card}>
        {phase === "auth" && (
          <>
            <div className={c.badge}><span className={c.badgeDot} /></div>
            <h1 className={c.h1}>Autorizar o MCP de chamadas</h1>
            <p className={c.sub}>
              Isto permite que seu assistente de IA (Claude, ChatGPT, Gemini) faça <b>ligações</b> pela sua conta
              Numera — inclusive com um agente de voz. Confirme o número de origem e autorize.
            </p>

            {info && info.user && (
              <div className={c.who}>
                <span className={c.whoName}>{info.user.name}</span>
                <span className={c.whoEmail}>{info.user.email}</span>
              </div>
            )}

            <label className={c.label}>Número de origem (Caller ID)</label>
            {!hasNumbers ? (
              <p className={c.sub}>Nenhum número encontrado na sua conta BR DID. Contrate um DID e volte aqui.</p>
            ) : (
              <>
                <div className={c.fixed}>
                  {did || "—"}
                  {selectedInfo && (selectedInfo.label || selectedInfo.city)
                    ? `  ·  ${[selectedInfo.label, selectedInfo.city].filter(Boolean).join(" · ")}`
                    : ""}
                </div>
                {urlDid && did === urlDid ? <div className={c.fixedNote}>Número indicado no acesso.</div> : null}
                <button className={c.linkBtn} onClick={() => setModalOpen(true)}>Escolher outro número</button>
              </>
            )}

            <button className={c.btn} disabled={!hasNumbers || !did} onClick={enable}>
              Autorizar e habilitar
            </button>
          </>
        )}

        {phase === "working" && (
          <>
            <h1 className={c.h1}>Configurando…</h1>
            <div style={{ marginTop: 16 }}>
              {STEPS.map((s, i) => (
                <div key={s} className={c.step}>
                  <span className={c.dot} style={{ background: i < stepIdx ? "#1E9E62" : i === stepIdx ? "#C77A12" : "#C7CDD3" }}>
                    {i < stepIdx ? "✓" : ""}
                  </span>
                  <span style={{ color: i <= stepIdx ? "#1B222A" : "#929AA4", fontWeight: i === stepIdx ? 600 : 400 }}>{s}…</span>
                </div>
              ))}
            </div>
          </>
        )}

        {phase === "done" && result && (
          <>
            <div className={c.badge}><span className={c.badgeDot} /></div>
            <h1 className={c.h1}>MCP habilitado</h1>
            <p className={c.sub}>
              {result.number ? <>As ligações sairão do número <b>{result.number}</b>. </> : null}
              Adicione o conector abaixo no seu assistente e <b>autorize</b> com sua conta — sem chave.
            </p>

            {result.number && !result.routed && (
              <div className={c.warn}>
                As ligações <b>de saída</b> já funcionam. O roteamento de <b>entrada</b> desse número na BR DID
                não foi aplicado — chamadas recebidas ainda não chegam aqui. Nosso time vai concluir.
              </div>
            )}

            <label className={c.label}>URL do MCP</label>
            <div className={c.urlBox}>
              <input className={c.urlInput} readOnly value={result.mcpUrl} onFocus={e => e.target.select()} />
              <button className={c.copy} onClick={() => copy(result.mcpUrl)}>Copiar</button>
            </div>

            <details className={c.det}>
              <summary className={c.detSummary}>Como adicionar no Claude</summary>
              <ol className={c.ol}>
                <li>Configurações → <b>Conectores</b> → <b>Adicionar conector personalizado</b>.</li>
                <li>Cole a URL do MCP e confirme.</li>
                <li>Clique em <b>Conectar</b> e <b>autorize</b> com sua conta.</li>
              </ol>
            </details>
            <details className={c.det}>
              <summary className={c.detSummary}>Como adicionar no ChatGPT</summary>
              <ol className={c.ol}>
                <li>Configurações → <b>Conectores</b> (ou Apps) → <b>Adicionar</b> servidor MCP.</li>
                <li>Informe a URL do MCP como servidor remoto.</li>
                <li><b>Autorize</b> o acesso quando pedir.</li>
              </ol>
            </details>
            <details className={c.det}>
              <summary className={c.detSummary}>Como adicionar no Gemini</summary>
              <ol className={c.ol}>
                <li>Extensões / <b>Conectores</b> → adicionar servidor MCP.</li>
                <li>Cole a URL do MCP.</li>
                <li>Faça login e <b>autorize</b> o conector.</li>
              </ol>
            </details>

            <button className={`${c.btn} ${c.btnGhost}`} onClick={() => { setResult(null); setPhase("auth"); }}>
              Trocar número
            </button>
          </>
        )}

        {phase === "error" && (
          <>
            <h1 className={c.h1}>Algo deu errado</h1>
            <div className={c.err}>{error}</div>
            <button className={`${c.btn} ${c.btnGhost}`} onClick={() => setPhase("auth")}>Tentar de novo</button>
          </>
        )}
      </div>

      <NumberModal
        open={modalOpen}
        numbers={(info && info.numbers) || []}
        value={did}
        onClose={() => setModalOpen(false)}
        onPick={setDid}
      />
    </div>
  );
};

export default McpSetup;
